import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  User, Building2, Trophy, Video, Monitor, FileBadge,
  Film, IndianRupee, Lock, FileCheck, Upload, Camera,
  ChevronDown, ChevronUp, X, Check, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SECTIONS = [
  { id: 'personal', num: '01', title: 'Personal Details', icon: User },
  { id: 'identity', num: '02', title: 'Coaching Identity', icon: Building2 },
  { id: 'sport', num: '03', title: 'Sport & Specialisation', icon: Trophy },
  { id: 'environment', num: '04', title: 'Recording Environment', icon: Monitor },
  { id: 'format', num: '05', title: 'Coaching Format', icon: Video },
  { id: 'credentials', num: '06', title: 'Credentials', icon: FileBadge },
  { id: 'sample', num: '07', title: 'Sample Coaching Video', icon: Film },
  { id: 'pricing', num: '08', title: 'Session Pricing', icon: IndianRupee },
  { id: 'private', num: '09', title: 'Private Contact & Bank', icon: Lock },
  { id: 'agreement', num: '10', title: 'Agreement', icon: FileCheck },
];

const RECORDING_LOCATIONS = [
  { id: 'indoor', label: 'Indoor (studio, room or sports hall)' },
  { id: 'outdoor', label: 'Outdoor (field or ground)' },
  { id: 'stadium', label: 'Stadium or arena' },
  { id: 'street', label: 'Street or open public space' },
  { id: 'poolside', label: 'Poolside or aquatic centre' },
  { id: 'court', label: 'Court (tennis, basketball, badminton)' },
];

const COACHING_FORMATS = [
  { id: 'recorded', label: 'Pre-recorded video courses' },
  { id: 'live-1on1', label: 'Live 1-on-1 online sessions' },
  { id: 'live-group', label: 'Live group online sessions' },
  { id: 'in-person-individual', label: 'In-person individual sessions' },
  { id: 'in-person-group', label: 'In-person group sessions' },
];

const STUDENT_LEVELS = [
  { id: 'beginner', label: 'Beginner' },
  { id: 'advanced', label: 'Advanced' },
  { id: 'professional', label: 'Professional' },
];

const AGE_GROUPS = [
  { id: 'kids', label: 'Kids (5–12)' },
  { id: 'teens', label: 'Teens (13–17)' },
  { id: 'adults', label: 'Adults (18–40)' },
  { id: 'masters', label: 'Masters (40+)' },
];

const initialForm = {
  // 1
  full_name: '',
  email: '',
  phone: '',
  profile_photo_url: '',
  // 2
  coaching_type: 'independent',
  academy_name: '',
  bio: '',
  // 3
  specializations: [],
  levels_coached: [],
  age_groups: [],
  experience_years: '',
  // 4
  recording_locations: [],
  // 5
  coaching_formats: [],
  // 6
  certifications: '',
  certificate_name: '',
  certificate_url: '',
  achievements: '',
  // 7
  sample_video_url: '',
  // 8
  session_rate: '',
  // 9
  bank_account_holder_name: '',
  bank_account_number: '',
  bank_ifsc: '',
  upi_id: '',
  // 10
  agreed_terms: false,
  agreed_authentic_content: false,
  agreed_code_of_conduct: false,
};

function BecomeCoach() {
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [activeSection, setActiveSection] = useState('personal');
  const [submitting, setSubmitting] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [certUploading, setCertUploading] = useState(false);
  const [videoUploading, setVideoUploading] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [sportsCategories, setSportsCategories] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState({});

  const sectionRefs = useRef({});

  useEffect(() => {
    axios.get(`${API}/sports/categories`).then(r => {
      setSportsCategories(r.data || []);
      const initial = {};
      (r.data || []).slice(0, 2).forEach(c => { initial[c.category_id] = true; });
      setExpandedCategories(initial);
    }).catch(() => {});

    // Try to prefill name/email from current session
    axios.get(`${API}/auth/me`, { withCredentials: true }).then(r => {
      const u = r.data || {};
      setForm(prev => ({
        ...prev,
        full_name: prev.full_name || u.name || '',
        email: prev.email || u.email || '',
        phone: prev.phone || u.phone || '',
        profile_photo_url: prev.profile_photo_url || u.profile_picture_url || '',
      }));
    }).catch(() => {});
  }, []);

  // Scroll-spy for sticky progress indicator
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter(e => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible.length) setActiveSection(visible[0].target.id);
      },
      { rootMargin: '-30% 0px -55% 0px', threshold: [0.1, 0.3, 0.6] }
    );
    SECTIONS.forEach(s => {
      const el = sectionRefs.current[s.id];
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const setField = (name, value) => setForm(prev => ({ ...prev, [name]: value }));
  const onChange = (e) => setField(e.target.name, e.target.type === 'checkbox' ? e.target.checked : e.target.value);

  const toggleInArray = (key, value, max = null) => {
    setForm(prev => {
      const arr = prev[key] || [];
      if (arr.includes(value)) return { ...prev, [key]: arr.filter(v => v !== value) };
      if (max && arr.length >= max) {
        toast.error(`You can select up to ${max} options`);
        return prev;
      }
      return { ...prev, [key]: [...arr, value] };
    });
  };

  const scrollToSection = (id) => {
    const el = sectionRefs.current[id];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const uploadToCloudinary = async (endpoint, file, onProgress) => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await axios.post(`${API}${endpoint}`, fd, {
      withCredentials: true,
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (evt) => {
        if (onProgress && evt.total) onProgress(Math.round((evt.loaded * 100) / evt.total));
      },
    });
    return res.data;
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) return toast.error('Please select an image file');
    setPhotoUploading(true);
    try {
      const data = await uploadToCloudinary('/upload/image', file);
      setField('profile_photo_url', data.url);
      toast.success('Profile photo uploaded');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Photo upload failed';
      toast.error(msg);
    } finally {
      setPhotoUploading(false);
    }
  };

  const handleCertUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCertUploading(true);
    try {
      const data = await uploadToCloudinary('/upload/document', file);
      setField('certificate_url', data.url);
      toast.success('Certificate uploaded');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Certificate upload failed';
      toast.error(msg);
    } finally {
      setCertUploading(false);
    }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const okType = ['video/mp4', 'video/quicktime', 'video/x-quicktime'].includes(file.type) ||
                   /\.(mp4|mov)$/i.test(file.name);
    if (!okType) return toast.error('Only MP4 or MOV videos are accepted');
    if (file.size > 500 * 1024 * 1024) return toast.error('Video must be 500MB or smaller');
    setVideoUploading(true);
    setVideoProgress(0);
    try {
      const data = await uploadToCloudinary('/upload/video', file, setVideoProgress);
      setField('sample_video_url', data.url);
      toast.success('Sample video uploaded');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Video upload failed';
      toast.error(msg);
    } finally {
      setVideoUploading(false);
    }
  };

  const validate = () => {
    const errors = [];
    if (!form.full_name.trim()) errors.push('Full name');
    if (!form.email.trim()) errors.push('Email');
    if (!form.phone.trim()) errors.push('Phone');
    if (!form.coaching_type) errors.push('Coaching identity');
    if (form.coaching_type === 'academy' && !form.academy_name.trim()) errors.push('Academy name');
    if (!form.bio.trim()) errors.push('Bio');
    if (form.specializations.length === 0) errors.push('At least one sport');
    if (form.levels_coached.length === 0) errors.push('Levels coached');
    if (form.age_groups.length === 0) errors.push('Age groups');
    if (!form.experience_years) errors.push('Experience years');
    if (form.recording_locations.length === 0) errors.push('Recording environment');
    if (form.coaching_formats.length === 0) errors.push('Coaching format');
    if (!form.sample_video_url) errors.push('Sample coaching video');
    if (!form.session_rate) errors.push('Session rate');
    if (!form.bank_account_holder_name.trim()) errors.push('Account holder');
    if (!form.bank_account_number.trim()) errors.push('Account number');
    if (!form.bank_ifsc.trim()) errors.push('IFSC');
    if (!form.upi_id.trim()) errors.push('UPI ID');
    if (!form.agreed_terms || !form.agreed_authentic_content || !form.agreed_code_of_conduct)
      errors.push('All three agreements');
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validate();
    if (errors.length) {
      toast.error(`Missing: ${errors.slice(0, 3).join(', ')}${errors.length > 3 ? '…' : ''}`);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        experience_years: parseInt(form.experience_years) || 0,
        session_rate: parseFloat(form.session_rate) || 0,
        certifications: form.certifications.split(',').map(c => c.trim()).filter(Boolean),
      };
      await axios.post(`${API}/users/become-coach`, payload, { withCredentials: true });
      toast.success('Coach application submitted successfully!');
      navigate('/coach/dashboard');
    } catch (err) {
      if (err.response?.status === 401) {
        const redirectUrl = window.location.origin + '/become-coach';
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
      } else {
        toast.error(err.response?.data?.detail || 'Failed to submit application');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const setRef = useCallback((id) => (el) => { sectionRefs.current[id] = el; }, []);

  return (
    <div className="min-h-screen bg-deep-dark" data-testid="become-coach-page">
      {/* Hero */}
      <div className="relative pt-20 pb-12 px-4 sm:px-6 lg:px-8 border-b border-premium-gold/10">
        <div className="max-w-5xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-premium-gold/40 bg-premium-gold/5 mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-premium-gold animate-pulse" />
            <span className="text-xs tracking-widest uppercase text-premium-gold font-semibold">Coach Application</span>
          </div>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-4"
            style={{ fontFamily: 'Fraunces, serif' }}
            data-testid="page-title"
          >
            Become a <span className="gold-gradient">DIOS</span> Coach
          </h1>
          <p className="text-base text-soft-lavender max-w-2xl">
            Tell us about your craft. This is a curated, premium platform — please complete every section thoughtfully.
          </p>
        </div>
      </div>

      {/* Layout: form + sticky stepper */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-12 gap-10">
        {/* Sticky Progress Indicator */}
        <aside className="hidden lg:block col-span-3" data-testid="coach-stepper">
          <div className="sticky top-24 space-y-1">
            <p className="text-xs tracking-widest uppercase text-soft-lavender mb-4">Progress</p>
            {SECTIONS.map((s) => {
              const active = activeSection === s.id;
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => scrollToSection(s.id)}
                  data-testid={`step-${s.num}`}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-all ${
                    active
                      ? 'bg-premium-gold/10 border border-premium-gold/40'
                      : 'border border-transparent hover:border-premium-gold/20'
                  }`}
                >
                  <span
                    className={`text-xs font-semibold tracking-widest ${
                      active ? 'text-premium-gold' : 'text-soft-lavender'
                    }`}
                  >
                    {s.num}
                  </span>
                  <span className={`text-sm ${active ? 'text-white' : 'text-soft-lavender'}`}>
                    {s.title}
                  </span>
                </button>
              );
            })}
          </div>
        </aside>

        {/* Form */}
        <form onSubmit={handleSubmit} className="col-span-12 lg:col-span-9 space-y-12">
          {/* SECTION 1: Personal Details */}
          <Section id="personal" refSet={setRef('personal')} num="01" title="Personal Details" icon={User}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Full Name" required>
                <input
                  type="text"
                  name="full_name"
                  value={form.full_name}
                  onChange={onChange}
                  className="dios-input"
                  placeholder="Your full legal name"
                  data-testid="full-name-input"
                />
              </Field>
              <Field label="Email" required>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={onChange}
                  className="dios-input"
                  placeholder="you@example.com"
                  data-testid="email-input"
                />
              </Field>
              <Field label="Phone" required>
                <input
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={onChange}
                  className="dios-input"
                  placeholder="+91 98xxxxxxxx"
                  data-testid="phone-input"
                />
              </Field>
              <Field label="Profile Photo" hint="Square image, min 400×400">
                <div className="flex items-center gap-4">
                  <div className="relative w-20 h-20 rounded-xl overflow-hidden bg-deep-dark border border-premium-gold/30 flex items-center justify-center shrink-0">
                    {form.profile_photo_url ? (
                      <img src={form.profile_photo_url} alt="Profile" className="w-full h-full object-cover" />
                    ) : (
                      <Camera className="w-7 h-7 text-soft-lavender" strokeWidth={1.5} />
                    )}
                  </div>
                  <label className="cursor-pointer flex items-center gap-2 px-4 py-2.5 rounded-lg border border-premium-gold/40 text-premium-gold hover:bg-premium-gold/5 transition" data-testid="upload-photo-btn">
                    {photoUploading ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</>
                    ) : (
                      <><Upload className="w-4 h-4" /> {form.profile_photo_url ? 'Replace photo' : 'Upload photo'}</>
                    )}
                    <input type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} disabled={photoUploading} />
                  </label>
                </div>
              </Field>
            </div>
          </Section>

          {/* SECTION 2: Coaching Identity */}
          <Section id="identity" refSet={setRef('identity')} num="02" title="Coaching Identity" icon={Building2}>
            <Field label="How do you coach?" required>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  { id: 'independent', title: 'Independent Coach', desc: 'I coach on my own' },
                  { id: 'academy', title: 'Academy / Institution', desc: 'I represent an academy' },
                ].map(opt => {
                  const active = form.coaching_type === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setField('coaching_type', opt.id)}
                      data-testid={`coaching-type-${opt.id}`}
                      className={`text-left p-4 rounded-xl border transition ${active
                        ? 'border-premium-gold bg-premium-gold/5'
                        : 'border-premium-gold/20 hover:border-premium-gold/40 bg-dark-surface'}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <p className="font-semibold text-white">{opt.title}</p>
                        <span className={`w-4 h-4 rounded-full border ${active ? 'bg-premium-gold border-premium-gold' : 'border-soft-lavender'}`} />
                      </div>
                      <p className="text-sm text-soft-lavender">{opt.desc}</p>
                    </button>
                  );
                })}
              </div>
            </Field>
            {form.coaching_type === 'academy' && (
              <Field label="Academy / Institution Name" required>
                <input
                  type="text"
                  name="academy_name"
                  value={form.academy_name}
                  onChange={onChange}
                  className="dios-input"
                  placeholder="e.g. Gopichand Badminton Academy"
                  data-testid="academy-name-input"
                />
              </Field>
            )}
            <Field label="Short Bio" required hint="A focused 2–4 sentence summary of who you coach and what makes you different.">
              <textarea
                name="bio"
                value={form.bio}
                onChange={onChange}
                rows={4}
                className="dios-input"
                placeholder="I coach intermediate to advanced badminton players, focusing on footwork mechanics and competitive strategy…"
                data-testid="bio-input"
              />
            </Field>
          </Section>

          {/* SECTION 3: Sport & Specialisation */}
          <Section id="sport" refSet={setRef('sport')} num="03" title="Sport & Specialisation" icon={Trophy}>
            <Field label="Sports you coach" required hint="Select every sport you can teach in depth.">
              <div className="space-y-2 max-h-96 overflow-y-auto bg-deep-dark/60 border border-premium-gold/20 rounded-xl p-3" data-testid="sports-picker">
                {sportsCategories.map(category => (
                  <div key={category.category_id} className="border-b border-premium-gold/10 last:border-0 pb-2 last:pb-0">
                    <button
                      type="button"
                      onClick={() => setExpandedCategories(p => ({ ...p, [category.category_id]: !p[category.category_id] }))}
                      className="w-full flex items-center justify-between py-2 px-2 rounded hover:bg-premium-gold/5"
                    >
                      <span className="font-semibold text-white flex items-center gap-2">
                        <span className="text-xl">{category.icon}</span>
                        {category.category_name}
                        <span className="text-xs text-soft-lavender">({category.sports.length})</span>
                      </span>
                      {expandedCategories[category.category_id]
                        ? <ChevronUp className="w-4 h-4 text-soft-lavender" />
                        : <ChevronDown className="w-4 h-4 text-soft-lavender" />}
                    </button>
                    {expandedCategories[category.category_id] && (
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-1 pl-2">
                        {category.sports.map(sport => {
                          const active = form.specializations.includes(sport.id);
                          return (
                            <button
                              key={sport.id}
                              type="button"
                              onClick={() => toggleInArray('specializations', sport.id)}
                              data-testid={`sport-${sport.id}`}
                              className={`px-3 py-2 text-xs font-semibold rounded-lg flex items-center gap-2 transition ${
                                active
                                  ? 'bg-premium-gold text-deep-dark'
                                  : 'bg-deep-dark text-white/80 border border-premium-gold/20 hover:border-premium-gold/50'
                              }`}
                            >
                              <span>{sport.icon}</span>
                              <span className="truncate">{sport.name}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {form.specializations.length > 0 && (
                <p className="mt-3 text-sm text-premium-gold">{form.specializations.length} sport(s) selected</p>
              )}
            </Field>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Levels you coach" required>
                <ChipGroup
                  options={STUDENT_LEVELS}
                  selected={form.levels_coached}
                  onToggle={(id) => toggleInArray('levels_coached', id)}
                  testIdPrefix="level"
                />
              </Field>
              <Field label="Age groups" required>
                <ChipGroup
                  options={AGE_GROUPS}
                  selected={form.age_groups}
                  onToggle={(id) => toggleInArray('age_groups', id)}
                  testIdPrefix="age"
                />
              </Field>
            </div>

            <Field label="Years of coaching experience" required>
              <input
                type="number"
                min="0"
                name="experience_years"
                value={form.experience_years}
                onChange={onChange}
                className="dios-input md:max-w-xs"
                placeholder="e.g. 5"
                data-testid="experience-input"
              />
            </Field>
          </Section>

          {/* SECTION 4: Recording Environment */}
          <Section
            id="environment"
            refSet={setRef('environment')}
            num="04"
            title="Video Recording Environment"
            icon={Monitor}
            subtitle="Where will you typically record your videos? (Choose up to 3)"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="recording-locations">
              {RECORDING_LOCATIONS.map(loc => {
                const active = form.recording_locations.includes(loc.id);
                const disabled = !active && form.recording_locations.length >= 3;
                return (
                  <button
                    key={loc.id}
                    type="button"
                    disabled={disabled}
                    onClick={() => toggleInArray('recording_locations', loc.id, 3)}
                    data-testid={`location-${loc.id}`}
                    className={`text-left p-4 rounded-xl border transition flex items-center justify-between gap-3 ${
                      active
                        ? 'border-premium-gold bg-premium-gold/5'
                        : disabled
                        ? 'border-premium-gold/10 bg-dark-surface opacity-40 cursor-not-allowed'
                        : 'border-premium-gold/20 hover:border-premium-gold/40 bg-dark-surface'
                    }`}
                  >
                    <span className="text-white text-sm">{loc.label}</span>
                    {active && <Check className="w-4 h-4 text-premium-gold shrink-0" />}
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-soft-lavender mt-3">{form.recording_locations.length}/3 selected</p>
          </Section>

          {/* SECTION 5: Coaching Format */}
          <Section
            id="format"
            refSet={setRef('format')}
            num="05"
            title="Coaching Format"
            icon={Video}
            subtitle="How do you deliver your coaching?"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="coaching-formats">
              {COACHING_FORMATS.map(fmt => {
                const active = form.coaching_formats.includes(fmt.id);
                return (
                  <button
                    key={fmt.id}
                    type="button"
                    onClick={() => toggleInArray('coaching_formats', fmt.id)}
                    data-testid={`format-${fmt.id}`}
                    className={`text-left p-4 rounded-xl border transition flex items-center justify-between gap-3 ${
                      active ? 'border-premium-gold bg-premium-gold/5' : 'border-premium-gold/20 hover:border-premium-gold/40 bg-dark-surface'
                    }`}
                  >
                    <span className="text-white text-sm">{fmt.label}</span>
                    {active && <Check className="w-4 h-4 text-premium-gold shrink-0" />}
                  </button>
                );
              })}
            </div>
          </Section>

          {/* SECTION 6: Credentials */}
          <Section id="credentials" refSet={setRef('credentials')} num="06" title="Credentials" icon={FileBadge} subtitle="Optional but strongly recommended for verification.">
            <Field label="Certificate name" hint="e.g. NIS Diploma, FIFA C License, World Athletics Level 2">
              <input
                type="text"
                name="certificate_name"
                value={form.certificate_name}
                onChange={onChange}
                className="dios-input"
                placeholder="Certificate title"
                data-testid="certificate-name-input"
              />
            </Field>
            <Field label="Upload certificate" hint="PDF or image">
              <div className="flex items-center gap-3 flex-wrap">
                <label className="cursor-pointer flex items-center gap-2 px-4 py-2.5 rounded-lg border border-premium-gold/40 text-premium-gold hover:bg-premium-gold/5 transition" data-testid="upload-cert-btn">
                  {certUploading ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</>
                  ) : (
                    <><Upload className="w-4 h-4" /> {form.certificate_url ? 'Replace certificate' : 'Upload certificate'}</>
                  )}
                  <input type="file" accept="image/*,application/pdf" className="hidden" onChange={handleCertUpload} disabled={certUploading} />
                </label>
                {form.certificate_url && (
                  <a href={form.certificate_url} target="_blank" rel="noreferrer" className="text-sm text-premium-gold underline">View uploaded file</a>
                )}
              </div>
            </Field>
            <Field label="Other certifications" hint="Comma-separated (optional)">
              <input
                type="text"
                name="certifications"
                value={form.certifications}
                onChange={onChange}
                className="dios-input"
                placeholder="Level 2 Cricket Coach, FIFA C License, …"
                data-testid="certifications-input"
              />
            </Field>
            <Field label="Notable achievements" hint="Tournaments coached, athletes mentored, awards.">
              <textarea
                name="achievements"
                value={form.achievements}
                onChange={onChange}
                rows={3}
                className="dios-input"
                placeholder="Coached 3 national-level athletes; produced two state champions in 2024…"
                data-testid="achievements-input"
              />
            </Field>
          </Section>

          {/* SECTION 7: Sample Coaching Video */}
          <Section
            id="sample"
            refSet={setRef('sample')}
            num="07"
            title="Sample Coaching Video"
            icon={Film}
            subtitle="A 2–5 minute video so we can see your coaching style. Required."
          >
            <div className="rounded-xl border border-premium-gold/20 bg-dark-surface p-5">
              {!form.sample_video_url ? (
                <div className="flex flex-col items-center text-center py-8">
                  <Film className="w-10 h-10 text-premium-gold mb-3" strokeWidth={1.2} />
                  <p className="text-white font-semibold mb-1">Upload your sample video</p>
                  <p className="text-soft-lavender text-sm mb-5">MP4 or MOV, up to 500MB</p>
                  <label className="cursor-pointer inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-premium-gold text-deep-dark font-bold tracking-wider hover:bg-premium-gold-light transition" data-testid="upload-video-btn">
                    {videoUploading ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Uploading {videoProgress}%</>
                    ) : (
                      <><Upload className="w-4 h-4" /> Choose video</>
                    )}
                    <input type="file" accept="video/mp4,video/quicktime,.mp4,.mov" className="hidden" onChange={handleVideoUpload} disabled={videoUploading} />
                  </label>
                  {videoUploading && (
                    <div className="w-full max-w-md mt-5">
                      <div className="h-2 bg-deep-dark rounded-full overflow-hidden">
                        <div className="h-full bg-premium-gold transition-all" style={{ width: `${videoProgress}%` }} />
                      </div>
                      <p className="text-xs text-soft-lavender mt-2">{videoProgress}% uploaded</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <video src={form.sample_video_url} controls className="w-full rounded-lg max-h-[420px] bg-black" data-testid="sample-video-preview" />
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-premium-gold/10 border border-premium-gold/40 text-premium-gold text-xs font-semibold">
                      <Check className="w-3 h-3" /> Uploaded
                    </span>
                    <button
                      type="button"
                      onClick={() => setField('sample_video_url', '')}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-soft-lavender/40 text-soft-lavender hover:text-white hover:border-white text-xs"
                      data-testid="remove-video-btn"
                    >
                      <X className="w-3 h-3" /> Replace
                    </button>
                  </div>
                </div>
              )}
            </div>
          </Section>

          {/* SECTION 8: Pricing */}
          <Section id="pricing" refSet={setRef('pricing')} num="08" title="Session Pricing" icon={IndianRupee} subtitle="Set the rate per coaching session. You can change this anytime.">
            <Field label="Session rate (INR)" required>
              <div className="relative md:max-w-xs">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-soft-lavender">₹</span>
                <input
                  type="number"
                  min="0"
                  step="50"
                  name="session_rate"
                  value={form.session_rate}
                  onChange={onChange}
                  className="dios-input pl-7"
                  placeholder="1500"
                  data-testid="session-rate-input"
                />
              </div>
            </Field>
          </Section>

          {/* SECTION 9: Private contact & bank */}
          <Section
            id="private"
            refSet={setRef('private')}
            num="09"
            title="Private Contact & Bank Details"
            icon={Lock}
            subtitle="Visible only to DIOS admins for verification & payouts."
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Account holder name" required>
                <input type="text" name="bank_account_holder_name" value={form.bank_account_holder_name} onChange={onChange} className="dios-input" placeholder="As per bank records" data-testid="account-holder-input" />
              </Field>
              <Field label="Account number" required>
                <input type="text" name="bank_account_number" value={form.bank_account_number} onChange={onChange} className="dios-input" placeholder="1234567890" data-testid="account-number-input" />
              </Field>
              <Field label="IFSC code" required>
                <input type="text" name="bank_ifsc" value={form.bank_ifsc} onChange={onChange} className="dios-input" placeholder="SBIN0001234" data-testid="ifsc-input" />
              </Field>
              <Field label="UPI ID" required>
                <input type="text" name="upi_id" value={form.upi_id} onChange={onChange} className="dios-input" placeholder="yourname@upi" data-testid="upi-input" />
              </Field>
            </div>
          </Section>

          {/* SECTION 10: Agreement */}
          <Section refSet={setRef('agreement')} num="10" title="Agreement" icon={FileCheck}>
            <div className="space-y-4" data-testid="agreement-section">
              <Checkbox
                name="agreed_terms"
                checked={form.agreed_terms}
                onChange={onChange}
                label="I agree to the DIOS Terms of Service and Privacy Policy."
                testId="agree-terms"
              />
              <Checkbox
                name="agreed_authentic_content"
                checked={form.agreed_authentic_content}
                onChange={onChange}
                label="I confirm that all content, credentials and videos I have submitted are authentic and represent my own coaching."
                testId="agree-authentic"
              />
              <Checkbox
                name="agreed_code_of_conduct"
                checked={form.agreed_code_of_conduct}
                onChange={onChange}
                label="I will follow the DIOS Coach Code of Conduct: respectful, safe and child-protective coaching at all times."
                testId="agree-conduct"
              />
            </div>

            <div className="mt-8 pt-6 border-t border-premium-gold/20">
              <button
                type="submit"
                disabled={submitting}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-premium-gold hover:bg-premium-gold-light text-deep-dark font-bold tracking-wider px-8 py-4 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="submit-application-btn"
              >
                {submitting ? (
                  <><Loader2 className="w-5 h-5 animate-spin" /> Submitting…</>
                ) : (
                  <>Submit Application</>
                )}
              </button>
              <p className="text-xs text-soft-lavender mt-3">
                We typically review new applications within 3–5 business days.
              </p>
            </div>
          </Section>
        </form>
      </div>
    </div>
  );
}

/* ---------- Local components ---------- */

function Section({ id, refSet, num, title, subtitle, icon: Icon, children }) {
  return (
    <section
      id={id}
      ref={refSet}
      className="scroll-mt-24"
      data-testid={`section-${num}`}
    >
      <div className="flex items-center gap-3 mb-6">
        <span className="text-sm tracking-widest text-premium-gold font-semibold">{num}</span>
        <span className="h-px flex-1 bg-premium-gold/20" />
        <Icon className="w-5 h-5 text-premium-gold" strokeWidth={1.5} />
      </div>
      <h2
        className="text-2xl sm:text-3xl font-bold text-white mb-2"
        style={{ fontFamily: 'Fraunces, serif' }}
      >
        {title}
      </h2>
      {subtitle && <p className="text-soft-lavender mb-6">{subtitle}</p>}
      <div className="space-y-5">{children}</div>
    </section>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-white mb-2">
        {label}
        {required && <span className="text-premium-gold ml-1">*</span>}
      </label>
      {children}
      {hint && <p className="text-xs text-soft-lavender mt-2">{hint}</p>}
    </div>
  );
}

function ChipGroup({ options, selected, onToggle, testIdPrefix }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map(opt => {
        const active = selected.includes(opt.id);
        return (
          <button
            key={opt.id}
            type="button"
            onClick={() => onToggle(opt.id)}
            data-testid={`${testIdPrefix}-${opt.id}`}
            className={`px-4 py-2 rounded-full text-sm border transition ${
              active
                ? 'bg-premium-gold text-deep-dark border-premium-gold'
                : 'bg-dark-surface text-white/80 border-premium-gold/30 hover:border-premium-gold/60'
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function Checkbox({ name, checked, onChange, label, testId }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer p-4 rounded-xl border border-premium-gold/20 bg-dark-surface hover:border-premium-gold/40 transition">
      <span className="relative mt-0.5 shrink-0">
        <input
          type="checkbox"
          name={name}
          checked={checked}
          onChange={onChange}
          className="peer sr-only"
          data-testid={`${testId}-checkbox`}
        />
        <span className="block w-5 h-5 rounded border border-premium-gold/40 bg-deep-dark peer-checked:bg-premium-gold peer-checked:border-premium-gold transition" />
        {checked && <Check className="w-3.5 h-3.5 text-deep-dark absolute top-0.5 left-0.5 pointer-events-none" strokeWidth={3} />}
      </span>
      <span className="text-sm text-white">{label}</span>
    </label>
  );
}

export default BecomeCoach;
